import './styles.css'

import type { DeliveryReconSiteOverviewRow, ConfigData, ConfigSectionProps } from '../types'

import { BBDTag, GraviButton, Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import { Collapse, Drawer, InputNumber } from 'antd'
import { useDeliveryRecon } from '@/api/useDeliveryReconciliation'
import { useEffect, useState } from 'react'
import { getInitialConfigValues, isCustomConfig } from '../util'
import { ReloadOutlined } from '@ant-design/icons'

function ConfigSection({ title, value, postTab, onChange, formatter, parser, width = 100 }: ConfigSectionProps) {
  return (
    <Vertical>
      <Horizontal gap='1em'>
        <Vertical gap='.5em'>
          <Texto weight='bold' category='h5'>
            {title}
          </Texto>
          <Horizontal verticalCenter gap='1em'>
            <Texto className='font-italic'>within</Texto>
            <InputNumber
              controls={false}
              step={postTab === '%' ? 0.01 : 1}
              formatter={formatter}
              parser={parser}
              type='number'
              size='small'
              addonAfter={postTab}
              style={{ width }}
              value={value}
              onChange={(value) => onChange(value as number)}
            />
          </Horizontal>
        </Vertical>
      </Horizontal>
    </Vertical>
  )
}

function ConfigSectionHeader({ title, isCustom }: { title: string; isCustom: boolean }) {
  return (
    <Horizontal verticalCenter justifyContent='space-between'>
      <Texto>{title}</Texto>
      <BBDTag theme2={isCustom} className='mr-2'>
        {isCustom ? 'Custom' : 'Global'}
      </BBDTag>
    </Horizontal>
  )
}

export function DeliveryConfigDrawer({
  open,
  configData,
  selectedRow,
  onClose,
}: {
  open: boolean
  configData: ConfigData
  selectedRow: DeliveryReconSiteOverviewRow | null
  onClose: () => void
}) {
  const { useSetThresholds, useSetConfig } = useDeliveryRecon()
  const [values, setValues] = useState<Partial<DeliveryReconSiteOverviewRow>>(
    getInitialConfigValues(selectedRow, configData)
  )

  const keys = Object.keys(values) as (keyof DeliveryReconSiteOverviewRow)[]
  const hasNoChanges = keys.every((key) => values[key] === selectedRow?.[key])
  const allFieldsFilled = keys.every((key) => values[key] != null && values[key] !== '')
  const shouldDisableSave = hasNoChanges || !allFieldsFilled

  const isMatchCustom = isCustomConfig(selectedRow, 'match', configData)
  const isApproveCustom = isCustomConfig(selectedRow, 'approve', configData)
  const isLoading = useSetThresholds.isPending || useSetConfig.isPending

  useEffect(() => {
    setValues(getInitialConfigValues(selectedRow, configData))
  }, [open, selectedRow, configData])

  async function handleSave() {
    try {
      selectedRow
        ? await useSetThresholds.mutateAsync({
            ...selectedRow,
            ...values,
          })
        : await useSetConfig.mutateAsync(values)
    } catch (error) {
      console.error('Error saving delivery config:', error)
    } finally {
      onClose()
    }
  }
  return (
    <Drawer
      width={450}
      destroyOnClose
      title='Delivery Reconciliation Config'
      closable={{ 'aria-label': 'Close Button' }}
      onClose={onClose}
      open={open}
      styles={{ body: { paddingTop: 8 }, footer: { backgroundColor: 'var(--bg-2)' } }}
      footer={
        <Horizontal verticalCenter justifyContent={selectedRow ? 'space-between' : 'end'} gap='1em'>
          {selectedRow && (
            <GraviButton
              color='primary'
              icon={<ReloadOutlined />}
              appearance='link'
              className='px-0'
              onClick={() => {
                try {
                  useSetThresholds.mutateAsync({ ...selectedRow, ...getInitialConfigValues() })
                } catch (error) {
                  console.error('Error resetting to global config:', error)
                } finally {
                  onClose()
                }
              }}
            >
              Reset to Global
            </GraviButton>
          )}
          <Horizontal>
            <GraviButton color='primary' appearance='link' onClick={onClose}>
              Cancel
            </GraviButton>
            <GraviButton color='primary' onClick={handleSave} loading={isLoading} disabled={shouldDisableSave}>
              Save Changes
            </GraviButton>
          </Horizontal>
        </Horizontal>
      }
    >
      <Vertical gap='1em'>
        <Horizontal alignItems='flex-start' justifyContent='space-between'>
          <Texto category='h5' weight='bold'>
            {selectedRow ? `${selectedRow.store} - ${selectedRow.product}` : 'Global'}
          </Texto>
        </Horizontal>
        <Collapse
          className='delivery-config-collapse'
          defaultActiveKey={['auto-match-config']}
          items={[
            {
              key: 'auto-match-config',
              label: <ConfigSectionHeader title='Auto Match Config' isCustom={isMatchCustom} />,
              children: (
                <Vertical gap='1em'>
                  <ConfigSection
                    title='By Adjusted Volume'
                    postTab='%'
                    value={values.delivery_match_volume_threshold}
                    formatter={(v) =>
                      v
                        ? Intl.NumberFormat(undefined, {
                            maximumFractionDigits: 2,
                            minimumFractionDigits: 0,
                          }).format(+v * 100)
                        : ''
                    }
                    parser={(v) => (v ? +v / 100 : '')}
                    onChange={(value) =>
                      setValues((prev) => ({ ...prev, delivery_match_volume_threshold: value ?? undefined }))
                    }
                    width={80}
                  />
                  <ConfigSection
                    title='By Time'
                    postTab='hours'
                    value={values.delivery_match_hour_threshold}
                    onChange={(value) =>
                      setValues((prev) => ({ ...prev, delivery_match_hour_threshold: value ?? undefined }))
                    }
                    width={90}
                  />
                </Vertical>
              ),
            },
          ]}
        />
        <Collapse
          className='delivery-config-collapse'
          defaultActiveKey={['auto-approve-config']}
          items={[
            {
              key: 'auto-approve-config',
              label: <ConfigSectionHeader title='Auto Approve Config' isCustom={isApproveCustom} />,
              children: (
                <Vertical gap='1em'>
                  <ConfigSection
                    title='By Adjusted Volume'
                    postTab='%'
                    value={values.delivery_approve_volume_threshold}
                    onChange={(value) =>
                      setValues((prev) => ({ ...prev, delivery_approve_volume_threshold: value ?? undefined }))
                    }
                    formatter={(v) =>
                      v
                        ? Intl.NumberFormat(undefined, {
                            maximumFractionDigits: 2,
                            minimumFractionDigits: 0,
                          }).format(+v * 100)
                        : ''
                    }
                    parser={(v) => (v ? +v / 100 : '')}
                    width={90}
                  />
                  <ConfigSection
                    title='By Time'
                    postTab='hours'
                    value={values.delivery_approve_hour_threshold}
                    onChange={(value) =>
                      setValues((prev) => ({ ...prev, delivery_approve_hour_threshold: value ?? undefined }))
                    }
                    width={110}
                  />
                </Vertical>
              ),
            },
          ]}
        />
      </Vertical>
    </Drawer>
  )
}
